/**
 * Processes incoming participant data from external sources.
 * Maps participant attributes and adds them to the tournament.
 */
import { tournamentEngine, tools, fixtures } from 'tods-competition-factory';
import { mutationRequest } from 'services/mutation/mutationRequest';
import { tmxToast } from 'services/notifications/tmxToast';
import { isFunction } from 'functions/typeOf';
import { hashCode } from 'functions/hashCode';
import { t } from 'i18n';

import { ADD_PARTICIPANTS, ADD_TOURNAMENT_EXTENSION } from 'constants/mutationConstants';
import { REGISTRATION } from 'constants/tmxConstants';

const modelParticipant = {
  participantName: ['full name', 'name'],
  participantId: ['id', 'participantid'],
  person: {
    standardFamilyName: ['last', 'last name', 'last_name'],
    standardGivenName: ['first', 'First Name', 'first_name'],
    nationalityCode: ['ioc', 'country', 'nationality'],
    address: { city: ['city'], state: ['state'] },
    birthDate: ['*birth'],
    sex: ['gender', 'sex'],
  },
};

export function incomingParticipants({
  data,
  sheetId,
  callback,
}: {
  data: any[];
  sheetId: string;
  callback?: () => void;
}): void {
  const existingParticipants = tournamentEngine.getParticipants().participants;
  const participantIds = new Set(existingParticipants.map(({ participantId }: any) => participantId));

  const validNationalityCodes = new Set(fixtures.countries.flatMap((f: any) => [f.ioc, f.iso]).filter(Boolean));

  const participants: any[] = [];

  const getAttributes = (attrs: string[]) =>
    attrs.flatMap((attr) => [attr.toLowerCase(), attr.toLowerCase().split(' ').join('')]);
  const findAttr =
    (row: any) =>
    (attrs: string[] = []) => {
      const keys = Object.keys(row);
      const attributes = getAttributes(attrs);
      const match = attributes.reduce((p, c) => row[c] || p, undefined);
      const partial =
        !match &&
        keys.filter((key) => attributes.find((a) => a.startsWith('*') && key.toLowerCase().includes(a.slice(1))));
      return match ?? row[partial[0]];
    };

  for (const row of data || []) {
    const loweredRow = Object.assign({}, ...Object.keys(row).map((key) => ({ [key.toLowerCase()]: row[key] })));
    const rowParser = findAttr(loweredRow);

    const participant: any = { participantType: 'INDIVIDUAL', participantRole: 'COMPETITOR', person: {} };
    const rawParticipantName = rowParser(modelParticipant.participantName);
    participant.participantName =
      typeof rawParticipantName === 'string' ? rawParticipantName.trim() : rawParticipantName;

    const tennisId = rowParser(['tennis id', 'tennisid']);
    if (tennisId) participant.person.tennisId = tennisId;

    const rawFamilyName = rowParser(modelParticipant.person.standardFamilyName);
    const rawGivenName = rowParser(modelParticipant.person.standardGivenName);
    const standardFamilyName = typeof rawFamilyName === 'string' ? rawFamilyName.trim() : rawFamilyName;
    const standardGivenName = typeof rawGivenName === 'string' ? rawGivenName.trim() : rawGivenName;
    if (standardFamilyName && standardGivenName) {
      participant.person.standardFamilyName = standardFamilyName;
      participant.person.standardGivenName = standardGivenName;
      participant.participantName = `${standardGivenName} ${standardFamilyName}`;
    } else {
      continue;
    }

    const state = rowParser(modelParticipant.person.address.state);
    const city = rowParser(modelParticipant.person.address.city);
    if (city || state) participant.person.addresses = [{ city, state }];

    const nationalityCode = rowParser(modelParticipant.person.nationalityCode);
    if (nationalityCode && validNationalityCodes.has(nationalityCode.toUpperCase())) {
      participant.person.nationalityCode = nationalityCode;
    }

    const birthDate = rowParser(modelParticipant.person.birthDate);
    if (tools.dateTime.dateValidation.test(birthDate)) {
      const date = tools.dateTime.extractDate(birthDate);
      if (date) participant.person.birthDate = date;
    }

    const sex = rowParser(modelParticipant.person.sex);
    if (sex) {
      const upperSex = sex.toUpperCase();
      if (['MALE', 'FEMALE'].includes(upperSex)) {
        participant.person.sex = upperSex;
      } else if (['F', 'W', 'G'].includes(sex[0])) {
        participant.person.sex = 'FEMALE';
      } else if (['M', 'B'].includes(sex[0])) {
        participant.person.sex = 'MALE';
      }
    }

    const utrProfile = rowParser(['utr profile']);
    if (utrProfile) {
      if (!participant.onlineResources) participant.onlineResources = [];
      const identifier = utrProfile.split('profiles/')?.reverse()[0];
      if (identifier) {
        if (!participant.person.personOtherIds) participant.person.personOtherIds = [];
        participant.person.personOtherIds.push({ uniqueOrganisationName: 'UTR', personId: identifier });
        if (utrProfile.startsWith('http')) {
          participant.onlineResources.push({
            resourceSubType: 'PROFILE',
            identifier: utrProfile,
            name: 'UTR Profile',
            resource: 'URL',
          });
        }
      }
    }

    const utr = rowParser(['utr']);
    if (utr) {
      if (!participant.timeItems) participant.timeItems = [];
      const timeItem = {
        itemType: 'SCALE.RATING.SINGLES.UTR',
        itemValue: { utrRating: utr },
      };
      participant.timeItems.push(timeItem);
    }
    const wtn = rowParser(['wtn']);
    if (wtn) {
      if (!participant.timeItems) participant.timeItems = [];
      const timeItem = {
        itemType: 'SCALE.RATING.SINGLES.WTN',
        itemValue: { wtnRating: wtn },
      };
      participant.timeItems.push(timeItem);
    }

    participant.participantId =
      rowParser(modelParticipant.participantId) || `XXX-${hashCode(participant.participantName)}`;

    const participantExists = participantIds.has(participant.participantId);
    if (!participantExists) {
      participants.push(tools.definedAttributes(participant));
    }
  }

  if (participants.length) {
    const methods = [
      {
        method: ADD_PARTICIPANTS,
        params: { participants },
      },
      {
        method: ADD_TOURNAMENT_EXTENSION,
        params: {
          extension: { name: REGISTRATION, value: sheetId },
        },
      },
    ];

    const postMutation = (result: any) => {
      if (result.success) {
        tmxToast({
          message: t('toasts.addedParticipants', { count: result.results[0].addedCount }),
          intent: 'is-success',
        });
        isFunction(callback) && callback();
      } else {
        console.log(result);
      }
    };
    mutationRequest({ methods, callback: postMutation });
  } else {
    tmxToast({ message: t('toasts.noNewParticipants'), intent: 'is-primary' });
  }

  isFunction(callback) && callback();
}
